import React from "react";
import { motion } from "framer-motion";
import { translations } from "../i18n/translations";
import { shareOrCopy } from "../utils/share";

/**
 * Tiny banner that pops in after a successful £2.99 seller-call Stripe redirect.
 * Eliminates the "did anything happen?" moment between landing on /report/:reg
 * and Nina's call status card actually appearing (10-20s of analysis loading).
 *
 * Listens for `worthify:nina-call-update` custom events broadcast by
 * NinaCopilot's status-poll loop so we can render a LIVE countdown progress
 * bar (0:00 → up to 8:00 max) right inside the toast. When the call reaches
 * a terminal state we swap copy/colour and self-dismiss after a short grace.
 */
export const SellerCallSuccessToast = ({ language }) => {
  const initial = React.useMemo(() => {
    if (typeof window === "undefined") return { visible: false, name: "" };
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("purpose") !== "seller_call") return { visible: false, name: "" };
      if (!params.get("call_session_id")) return { visible: false, name: "" };
      let name = "";
      try { name = localStorage.getItem("worthifyLastSellerName") || ""; } catch { /* ignore */ }
      return { visible: true, name };
    } catch { return { visible: false, name: "" }; }
  }, []);
  const [visible, setVisible] = React.useState(initial.visible);
  const sellerName = initial.name;
  // Live call state, fed by NinaCopilot's status poller via window events.
  const [callState, setCallState] = React.useState({
    status: "connecting", // connecting | in_progress | completed | failed | cancelled
    duration_seconds: 0,
    max_minutes: 8,
    outcome: null,
  });

  // Listen for status updates from NinaCopilot.
  React.useEffect(() => {
    if (!visible) return undefined;
    const onUpdate = (e) => {
      const d = (e?.detail) || {};
      setCallState((prev) => ({
        status: d.status || prev.status,
        duration_seconds: d.duration_seconds ?? prev.duration_seconds,
        max_minutes: d.max_minutes || prev.max_minutes,
        outcome: d.outcome ?? prev.outcome,
      }));
    };
    window.addEventListener("worthify:nina-call-update", onUpdate);
    return () => window.removeEventListener("worthify:nina-call-update", onUpdate);
  }, [visible]);

  // Smooth client-side tick so the timer ticks every second between server polls.
  React.useEffect(() => {
    if (!visible) return undefined;
    if (callState.status === "completed" || callState.status === "failed" || callState.status === "cancelled") {
      return undefined;
    }
    const id = setInterval(() => {
      setCallState((prev) => ({ ...prev, duration_seconds: (prev.duration_seconds || 0) + 1 }));
    }, 1000);
    return () => clearInterval(id);
  }, [visible, callState.status]);

  // Auto-dismiss policy: short fade for connecting (12s if no event), longer
  // grace for terminal so the user can see the outcome line.
  React.useEffect(() => {
    if (!visible) return undefined;
    if (callState.status === "completed" || callState.status === "failed" || callState.status === "cancelled") {
      const id = setTimeout(() => setVisible(false), 8000);
      return () => clearTimeout(id);
    }
    if (callState.status === "connecting") {
      // Auto-hide if no status arrives within 60s (something went wrong).
      const id = setTimeout(() => setVisible(false), 60000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [visible, callState.status]);

  if (!visible) return null;
  const t = (k, fallback) => (translations?.[language]?.[k] || fallback);

  const fmtTime = (s) => {
    const total = Math.max(0, Math.floor(s || 0));
    const m = Math.floor(total / 60);
    const r = total % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };
  const maxSeconds = (callState.max_minutes || 8) * 60;
  const pct = Math.min(100, Math.round(((callState.duration_seconds || 0) / maxSeconds) * 100));

  // Theme / copy by status
  const isCompleted = callState.status === "completed";
  const isFailed = callState.status === "failed" || callState.status === "cancelled";
  const inProgress = !isCompleted && !isFailed;
  const oc = callState.outcome;
  const success = isCompleted && oc?.success && oc?.agreed_price;
  const savings = oc?.savings || (oc?.original_price && oc?.agreed_price ? Math.max(0, oc.original_price - oc.agreed_price) : 0);

  let icon = "🎉";
  let headline = t("ninaToastPaid", "Payment confirmed");
  let body = sellerName
    ? t("ninaToastCallingNamed", "Nina is now calling {name}…").replace("{name}", sellerName)
    : t("ninaToastCallingGeneric", "Nina is now calling the seller…");

  if (callState.status === "in_progress") {
    icon = "📞";
    headline = t("ninaToastInProgress", "Nina is on the call");
    body = sellerName
      ? t("ninaToastNegotiatingNamed", "Negotiating with {name} now…").replace("{name}", sellerName)
      : t("ninaToastNegotiatingGeneric", "Negotiating with the seller now…");
  }
  if (isCompleted) {
    if (success && savings > 0) {
      icon = "💰";
      headline = t("ninaToastDealHeadline", "Deal agreed!");
      body = t("ninaToastSavedYou", "You saved £{amount}").replace("{amount}", Number(savings).toLocaleString("en-GB"));
    } else {
      icon = "✅";
      headline = t("ninaToastCallComplete", "Call complete");
      body = t("ninaToastCallCompleteSub", "Open Nina for the full transcript.");
    }
  }
  if (isFailed) {
    icon = "⚠️";
    headline = t("ninaToastCallFailed", "Call could not connect");
    body = t("ninaCallFailedHint", "The seller didn't pick up. You haven't been charged again.");
  }

  const handleShareWin = async (ev) => {
    ev.stopPropagation();
    const amountStr = Number(savings).toLocaleString("en-GB");
    const shareText = t("ninaShareWinText", "I just saved £{amount} on a used car with Worthify Nina 📞 — the AI co-pilot that calls the seller and negotiates for you.")
      .replace("{amount}", amountStr);
    const url = (typeof window !== "undefined") ? window.location.origin : "https://worthify.co.uk";
    await shareOrCopy({
      title: t("ninaShareWinTitle", "I saved £{amount} with Worthify").replace("{amount}", amountStr),
      text: shareText,
      url,
    });
  };

  // Gradient theme
  let gradient = "linear-gradient(135deg, rgba(34,197,94,0.96) 0%, rgba(16,185,129,0.96) 100%)";
  let glow = "0 18px 42px rgba(16,185,129,0.42)";
  if (isFailed) {
    gradient = "linear-gradient(135deg, rgba(220,38,38,0.96) 0%, rgba(185,28,28,0.96) 100%)";
    glow = "0 18px 42px rgba(220,38,38,0.40)";
  } else if (success) {
    gradient = "linear-gradient(135deg, rgba(245,158,11,0.97) 0%, rgba(217,119,6,0.97) 100%)";
    glow = "0 18px 42px rgba(245,158,11,0.45)";
  }

  return (
    <motion.div
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -80, opacity: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      onClick={() => setVisible(false)}
      role="status"
      data-testid="seller-call-success-toast"
      style={{
        position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)",
        width: "min(440px, calc(100vw - 24px))",
        zIndex: 10000,
        background: gradient,
        color: "var(--wf-modal-text)", borderRadius: 16, padding: "12px 14px 10px",
        boxShadow: `${glow}, 0 0 0 1px rgba(255,255,255,0.20)`,
        cursor: "pointer", backdropFilter: "blur(8px)",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          flexShrink: 0, width: 36, height: 36, borderRadius: 999,
          background: "rgba(255,255,255,0.22)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
        }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 800, fontSize: 13, letterSpacing: "-0.1px" }}>
            {headline}
          </p>
          <p style={{ margin: 0, fontSize: 12, opacity: 0.94, lineHeight: 1.4 }}>
            {body}
          </p>
        </div>
        {inProgress && (
          <div style={{
            flexShrink: 0, fontSize: 13, fontWeight: 800, fontVariantNumeric: "tabular-nums",
            background: "rgba(255,255,255,0.18)", padding: "4px 10px", borderRadius: 999,
            letterSpacing: "0.04em",
          }} data-testid="nina-toast-timer">
            {fmtTime(callState.duration_seconds)}
          </div>
        )}
        {success && savings > 0 && (
          <motion.button
            whileTap={{ scale: 0.94 }}
            onClick={handleShareWin}
            style={{
              flexShrink: 0, border: "none", cursor: "pointer",
              background: "rgba(255,255,255,0.22)", color: "var(--wf-modal-text)",
              fontWeight: 800, fontSize: 11.5, letterSpacing: "0.04em",
              padding: "6px 11px", borderRadius: 999,
              display: "flex", alignItems: "center", gap: 5,
            }}
            data-testid="nina-toast-share-win"
            aria-label={t("ninaShareWinCta", "Share win")}
          >
            <span aria-hidden style={{ fontSize: 13 }}>↗</span>
            {t("ninaShareWinCta", "Share win")}
          </motion.button>
        )}
      </div>

      {/* Live progress bar — only while the call is mid-flight. */}
      {inProgress && (
        <div style={{
          marginTop: 8, height: 4, borderRadius: 999,
          background: "rgba(255,255,255,0.20)", overflow: "hidden",
        }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{
              height: "100%", background: "rgba(255,255,255,0.95)",
              boxShadow: "0 0 8px rgba(255,255,255,0.6)",
              borderRadius: 999,
            }}
            data-testid="nina-toast-progress"
          />
        </div>
      )}
    </motion.div>
  );
};

export default SellerCallSuccessToast;
